import React from "react";
import styled from "styled-components";

/**
 * Modern Pager component using hasNextPage pattern
 * Clean, minimal design with better UX
 */

const PagerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
  gap: 8px;
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${props => props.$active ? '#7367f0' : '#e5e7eb'};
  background: ${props => props.$active ? '#7367f0' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.15s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#5a4fcf' : '#f9fafb'};
    border-color: ${props => props.$active ? '#5a4fcf' : '#d1d5db'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.2);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: #6b7280;
  margin: 0 16px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

export default function Pager({ 
  handleSetPage, 
  currentPage, 
  hasNextPage = null,
  // Legacy props for backward compatibility
  totalResults = null,
  resultsPerPage = null,
}) {
  // Prioritize hasNextPage if explicitly passed (not null/undefined)
  // Otherwise fall back to legacy totalResults calculation
  const calculatedHasNextPage = hasNextPage != null
    ? hasNextPage 
    : (totalResults != null && resultsPerPage != null)
      ? currentPage < Math.ceil(totalResults / resultsPerPage)
      : false;

  
  const hasPrevPage = currentPage > 1;

  const ChevronLeft = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
    </svg>
  );

  const ChevronRight = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
    </svg>
  );

  const ChevronsLeft = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.06 1.06L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.06 1.06L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
    </svg>
  );

  return (
    <PagerContainer>
      {/* First page */}
      <PageButton 
        onClick={() => handleSetPage(1)} 
        disabled={!hasPrevPage}
        title="Primera página"
      >
        <ChevronsLeft />
      </PageButton>

      {/* Previous page */}
      <PageButton 
        onClick={() => handleSetPage(currentPage - 1)} 
        disabled={!hasPrevPage}
        title="Página anterior"
      >
        <ChevronLeft />
      </PageButton>

      {/* Page info */}
      <PageInfo>
        Página <strong>{currentPage}</strong>
      </PageInfo>

      {/* Next page */}
      <PageButton 
        onClick={() => handleSetPage(currentPage + 1)} 
        disabled={!calculatedHasNextPage}
        title="Página siguiente"
      >
        <ChevronRight />
      </PageButton>
    </PagerContainer>
  );
}
